import React, { useCallback, useEffect, useState } from 'react';
import { OptionFieldType } from './option-field.types';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  RadioGroup,
  SelectChangeEvent,
} from '@mui/material';
import { useFormValidation } from '../context/FormValidation';
import { useSingleForm } from '../context/SingleForm';

export const CheckboxOptionField: React.FC<OptionFieldType> = ({
  optionGroupId,
  options,
  required,
  name,
  description,
  onChangeFieldValue,
  minOptions,
  maxOptions,
  defaultValues = [],
}) => {
  const getPrefilledValues = (): number[] => {
    return (
      options
        ?.filter(option => option.optionId && defaultValues.includes(option.optionId))
        .map(option => Number(option.optionId)) || []
    );
  };
  const [checkedValues, setCheckedValues] = useState<number[]>(getPrefilledValues());
  const [isValid, setIsValid] = useState<boolean>(false);
  const { showError, updateFieldValidation } = useFormValidation();
  const { formIndex } = useSingleForm();

  const isValidValue = useCallback(
    (value: number[]): boolean => {
      if (required) {
        return value.length >= minOptions && value.length <= maxOptions;
      }
      return value.length <= maxOptions;
    },
    [required, minOptions, maxOptions]
  );

  useEffect(() => {
    const isInitValid = isValidValue(getPrefilledValues());
    updateFieldValidation(`${formIndex}-${optionGroupId}`, isInitValid);
    setIsValid(isInitValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      let newValues: number[];
      if (checkedValues.includes(Number(event.target.value))) {
        newValues = checkedValues.filter(value => value !== Number(event.target.value));
      } else {
        newValues = checkedValues.concat([Number(event.target.value)]);
      }
      setCheckedValues(newValues);
      const isValid = isValidValue(newValues);
      setIsValid(isValid);
      updateFieldValidation(`${formIndex}-${optionGroupId}`, isValid);
      onChangeFieldValue(optionGroupId, newValues);
    },
    [onChangeFieldValue, optionGroupId, checkedValues, setCheckedValues, isValidValue, updateFieldValidation, formIndex]
  );

  return (
    <FormControl error={showError && !isValid}>
      <FormLabel id={`${formIndex}-${optionGroupId}`} className={required ? ' label-required' : ''}>
        {`${name} (Min: ${minOptions}, Max: ${maxOptions})`}
      </FormLabel>
      <RadioGroup aria-labelledby={optionGroupId.toString()} value={checkedValues}>
        {options?.map(option => {
          return (
            <FormControlLabel
              key={option.optionId}
              value={option.optionId}
              control={
                <Checkbox
                  required={required}
                  onChange={onChange}
                  checked={checkedValues.includes(Number(option.optionId))}
                />
              }
              label={`${option.name} (${Number(option.price).toFixed(2)} ${option.currency})`}
            />
          );
        })}
      </RadioGroup>
      <FormHelperText>{description}</FormHelperText>
    </FormControl>
  );
};
